/**
 * Auth: a person signs in with their work e-mail and password and gets a **session token** (a day,
 * renewed while they work, a week at most) from the service (`POST /v1/ui/login`). Nothing permanent ever reaches the browser.
 *
 * State lives in `localStorage`, **shared by every tab** (owner, 2026-10-06: "сесію між вкладками треба
 * зберігати"). It was `sessionStorage` — one tab, one session — so every new tab asked for a sign-in, which
 * read as "it keeps throwing me out". Tabs stay in step through the `storage` event (`main.tsx`): a
 * renewal in one is adopted by the others, a sign-out in one signs out all. What must stay per tab — which
 * tab holds a phone — is `tabId.ts`, still in `sessionStorage`. The token is renewed while someone
 * works (`expiry.ts`) and refused by the server past a ceiling, at which point the workspace says so
 * and offers the sign-in screen again.
 *
 * `dev` mode: a service token typed by hand (`source: 'manual'`) still works, for running against a
 * service with no users seeded. It has no expiry we know about and is never renewed.
 */

import type { TokenSource } from '../api/client'
import type { LoginResponse, User } from '../api/types'

export type AuthSource = 'password' | 'manual'

export interface AuthState {
  token: string
  user: User | null
  expiresAt: number | null
  source: AuthSource
  /** when this token was issued, by our clock — the renewal timer counts half-hours from it */
  issuedAt?: number
}

export const STORE_KEY = 'tthub_ui_auth'

export class Auth implements TokenSource {
  private state: AuthState | null = null

  token(): string {
    return this.state?.token ?? ''
  }

  get user(): User | null {
    return this.state?.user ?? null
  }

  get ready(): boolean {
    return Boolean(this.state?.token)
  }

  get source(): AuthSource | null {
    return this.state?.source ?? null
  }

  get expiresAt(): number | null {
    return this.state?.expiresAt ?? null
  }

  get issuedAt(): number | undefined {
    return this.state?.issuedAt
  }

  /** Admin or a service token: every gate on the service lets them through (`isAdminState`). */
  get isAdmin(): boolean {
    return isAdminState(this.state)
  }

  /** May this person change or delete a record `owner` created? Admins may; a tester only their own. */
  mayTouch(owner: string | null | undefined): boolean {
    return mayTouchState(this.state, owner)
  }

  /** Restore a session from this tab's storage. Returns true when we have something usable. */
  boot(): boolean {
    // A tab signed in before sessions were shared keeps its sign-in: moved over once.
    const legacy = sessionStorage.getItem(STORE_KEY)
    if (legacy && !localStorage.getItem(STORE_KEY)) localStorage.setItem(STORE_KEY, legacy)
    sessionStorage.removeItem(STORE_KEY)
    const stored = localStorage.getItem(STORE_KEY)
    if (!stored) {
      this.state = null
      return false
    }
    try {
      const parsed = JSON.parse(stored) as AuthState
      if (parsed.token && (parsed.expiresAt === null || parsed.expiresAt > Date.now())) {
        this.state = parsed
        return true
      }
    } catch {
      /* corrupt → start over */
    }
    localStorage.removeItem(STORE_KEY)
    this.state = null
    return false
  }

  /** What `POST /v1/ui/login` (or `/refresh`) answered. */
  accept(res: LoginResponse): void {
    this.set({
      token: res.token,
      user: res.user ?? this.state?.user ?? null,
      expiresAt: expiryOf(res),
      source: 'password',
      issuedAt: Date.now(),
    })
  }

  /** Replace the token with a renewed one, keeping who it is for. Written straight to storage like any
   *  other state change, so a reload during a shift picks up the extended deadline. */
  renew(res: LoginResponse): void {
    if (!this.state) return
    this.set({ ...this.state, token: res.token, expiresAt: expiryOf(res), issuedAt: Date.now(),
               user: res.user ?? this.state.user })
  }

  /** The person as the service has them now (`/v1/ui/me`) — role and directions an admin changed after
   *  this tab signed in. Keeps the token. */
  updateUser(user: User): void {
    if (!this.state) return
    this.set({ ...this.state, user })
  }

  /** Dev/ops path: a service token pasted by hand. */
  setManualToken(token: string): void {
    this.set({ token: token.trim(), user: null, expiresAt: null, source: 'manual' })
  }

  clear(): void {
    this.state = null
    localStorage.removeItem(STORE_KEY)
  }

  private set(state: AuthState): void {
    this.state = state
    localStorage.setItem(STORE_KEY, JSON.stringify(state))
  }
}

/** The server gives both an absolute time and a TTL; the TTL is measured against *our* clock, which is the
 *  one the renewal timer will read. A skewed machine clock would otherwise sign people out early or late. */
function expiryOf(res: { expires_in_s?: number; expires_at?: number }): number | null {
  if (typeof res.expires_in_s === 'number') return Date.now() + res.expires_in_s * 1000
  if (typeof res.expires_at === 'number') return res.expires_at * 1000
  return null
}

/**
 * **Who the workspace treats as an admin.** Two roles (`admin`, `tester`) ride in the session's user;
 * a **service token counts as admin**, because the service lets it through every `require_role`
 * gate — showing it a tester's screen would be the mistake Device Lab documents in reverse (an owner
 * arriving as a tester read working gates as a broken screen). Pure, so it is testable in Node.
 * This decides what is **offered**, never what is allowed: the gates are in the service.
 */
export function isAdminState(state: Pick<AuthState, 'source' | 'user'> | null | undefined): boolean {
  if (!state) return false
  if (state.source === 'manual') return true
  return state.user?.role === 'admin'
}

/** The service answers 403 `not_yours` when a tester touches somebody else's video, source, post or
 *  distribution. A record without an owner (older rows) is left to the service to judge. */
export function mayTouchState(
  state: Pick<AuthState, 'source' | 'user'> | null | undefined,
  owner: string | null | undefined,
): boolean {
  if (isAdminState(state)) return true
  if (!owner) return true
  return Boolean(state?.user?.email) && state?.user?.email === owner
}
